/**
 * AttemptMenu - "what happened on that call?" for one lead on a study's recruitment list
 * (docs/plans/crm-recruitment-list-plan.md Part A section 3 step 5 and section 6, B2
 * logRecruitAttempt, B4, B5).
 *
 * The split-button shape of outreach/OutcomeControl.tsx, rebuilt for
 * POST /api/crm/recruit/studies/:studyId/rows/:contactId/attempt (OutcomeControl itself is not
 * imported and not touched). The main half logs "No answer" in one click; the menu has Voicemail,
 * Talked, Call back at... (a REAL booked callback: the quick picks of workQueueLogic callbackPicks,
 * and a time outside calling hours comes back 422 and is booked only after "Book it anyway"),
 * Try later... (a snooze, workQueueLogic retryPicks, at most 60 days out), Wrong number, and Asked
 * us to stop (confirmed first: it opts them out of texts). Status changes are NOT here: the
 * status cell owns them, so the two paths can never disagree.
 *
 * The attempt is written FIRST; only a write that landed changes anything on screen. The control
 * stays busy until the write and the host's onLogged have settled, and the confirmation line is
 * built from the server's `effect` alone (recruitLogic attemptConfirmation), so it never claims
 * more than happened. A logged attempt never moves a Next-up session on (plan B5: a call often has
 * two outcomes, "Talked" then a status change); the host decides that itself.
 *
 * Two surfaces. `sheet` (the Attempts and Callback cells, and the cards): the menu and the
 * confirmation line are position:fixed and anchored to the control, because the sheet scrolls
 * sideways inside an overflow box that would clip them (the SnoozeButton precedent); a scroll or
 * a resize closes them. `bar` (the Next-up bar, plan B5): from md up the menu hangs below the
 * control the way OutcomeControl's does. Below md the bar's pill wraps and this control starts a
 * right-aligned row, where a 288px menu under it would run off a phone's left edge, so the menu
 * hangs from the whole bar instead, right-aligned under it like the bar's snooze menu: the bar is
 * translated, so a fixed child resolves against the bar, not the viewport.
 *
 * When `triesDisabledReason` is set (the lead is closed on this study, archived or merged) the
 * tries are unavailable and say why; Wrong number and Asked us to stop stay, because they are
 * recorded on the PERSON even after the row has closed (Part A section 6).
 */
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { ArrowLeft, CalendarClock, Check, ChevronDown, Clock, Loader2, PhoneOff } from 'lucide-react';
import { Z, useConfirm } from '../components/ui';
import { conflictHeadline, conflictLine, overbookConflictsOf } from '../components/callbackSlots/slotLogic';
import { notifyStatsChanged } from '../lib';
import { callbackPicks, pickedTimeProblem, retryPicks } from '../workQueueLogic';
import { logRecruitAttempt, recruitErrorCode, recruitErrorMessage } from './api';
import { ATTEMPT_LABELS, ATTEMPT_MENU, attemptConfirmation } from './recruitLogic';
import type { RecruitAttemptOutcome, RecruitAttemptRequest, RecruitAttemptResponse } from './types';

/** Where the menu is mounted: the sheet's cells and the cards, or the Next-up bar. */
export type AttemptSurface = 'sheet' | 'bar';

export interface AttemptMenuProps {
  studyId: string;
  contactId: string;
  /** Sets its size and how the menu is positioned (see the header). */
  surface: AttemptSurface;
  /** The study's cadence ceiling, for "Attempt 3 of 6." in the confirmation line. */
  maxAttempts?: number | null;
  /** 'book' renders only a "Book" link that opens the call-back step (the Callback cell). */
  variant?: 'split' | 'book';
  /** When set, the tries are unavailable and this sentence says why (Wrong number and Asked us to stop stay). */
  triesDisabledReason?: string | null;
  disabled?: boolean;
  /** Sheet keyboard: every new value opens the menu (Enter on the Attempts cell). */
  openSignal?: number;
  /** The menu closed (the sheet hands its keyboard cursor back). */
  onClose?: () => void;
  /**
   * Runs once the attempt is saved, with the request that was sent: the sheet applies `effect` to
   * the row. May return a promise; the control stays busy until it settles.
   */
  onLogged?: (
    outcome: RecruitAttemptOutcome,
    res: RecruitAttemptResponse,
    request: RecruitAttemptRequest,
  ) => void | Promise<void>;
  /** Host the confirmation / error line yourself (the bar). Omitted = the control shows it. */
  onFlash?: (tone: 'ok' | 'error', text: string) => void;
  /** The lead's name, for the accessible labels. */
  name?: string;
}

/** The outcomes that are a TRY (a call made, or the next one planned); the other two stop contact. */
export const TRY_OUTCOMES: ReadonlySet<RecruitAttemptOutcome> = new Set<RecruitAttemptOutcome>([
  'no_answer',
  'voicemail',
  'talked',
  'call_back',
  'try_later',
]);

type View = 'main' | 'call_back' | 'try_later';

interface Anchor {
  top: number;
  left: number;
}

interface Flash {
  tone: 'ok' | 'error';
  text: string;
  /** Where a `sheet` line sits (fixed); null = hung below the control. */
  at: Anchor | null;
}

/** How long the confirmation line stays up. Long enough to read on the way to the next call. */
const FLASH_MS = 6_000;
/** w-72: the menu's width, used to anchor it before it has been measured. */
const MENU_WIDTH = 288;
/** The confirmation line's widest (max-w-[18rem]) and a two-line height, for anchoring it. */
const FLASH_WIDTH = 288;
const FLASH_HEIGHT = 48;

/** Local-time value for an `<input type="datetime-local">` (its `min`). */
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "Tue, Sep 29, 10:00 AM": a quick pick's secondary line. */
function whenLabel(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Viewport coordinates for a fixed popover under `el`, its right edge on the control's right
 * edge, kept 8px inside the viewport, and flipped above the control when it would run off the
 * bottom. Null when there is no element.
 */
function anchorBelow(el: HTMLElement | null, width: number, height: number): Anchor | null {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  const vw = window.innerWidth || 0;
  const vh = window.innerHeight || 0;
  const left = Math.max(8, Math.min(r.right - width, vw - width - 8));
  let top = r.bottom + 6;
  if (height > 0 && vh > 0 && top + height > vh - 8) top = Math.max(8, r.top - height - 6);
  return { top: Math.round(top), left: Math.round(left) };
}

const SIZE: Record<AttemptSurface, { main: string; more: string }> = {
  bar: {
    main: 'min-h-[44px] w-[7rem] rounded-l-full border-0 bg-primary-600 pl-3 pr-2 text-xs text-white hover:bg-primary-700 sm:min-h-0 sm:py-1.5',
    more: 'min-h-[44px] rounded-r-full border-0 border-l border-primary-500 bg-primary-600 px-2 text-xs text-white hover:bg-primary-700 dark:border-primary-700 sm:min-h-0 sm:py-1.5',
  },
  sheet: {
    main: 'min-h-[44px] rounded-l-md border border-gray-300 bg-white pl-2 pr-1.5 text-xs text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 sm:h-7 sm:min-h-0',
    more: '-ml-px min-h-[44px] rounded-r-md border border-gray-300 bg-white px-1.5 text-xs text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 sm:h-7 sm:min-h-0',
  },
};

const BOOK_CLS =
  'inline-flex min-h-[44px] items-center gap-1 rounded-md px-1.5 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:h-7 sm:min-h-0';

const menuItemCls =
  'flex min-h-[44px] w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus-visible:bg-primary-50 focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-200 dark:hover:bg-primary-900/20 dark:focus-visible:bg-primary-900/20 sm:min-h-0 sm:py-1.5';

const fieldCls =
  'min-h-[44px] w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 transition-colors duration-150 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 sm:min-h-0';

const groupLabelCls =
  'px-2.5 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500';

export function AttemptMenu({
  studyId,
  contactId,
  surface,
  maxAttempts = null,
  variant = 'split',
  triesDisabledReason = null,
  disabled = false,
  openSignal,
  onClose,
  onLogged,
  onFlash,
  name,
}: AttemptMenuProps) {
  const confirm = useConfirm();
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>('main');
  const [picked, setPicked] = useState('');
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const [busy, setBusy] = useState<RecruitAttemptOutcome | null>(null);
  // The same flag, read synchronously: two clicks inside one render would both see `busy` null.
  const busyRef = useRef(false);
  const [flash, setFlash] = useState<Flash | null>(null);

  const fixed = surface === 'sheet';
  const book = variant === 'book';
  const triesOff = !!triesDisabledReason;

  // The host's callbacks, read at call time so they never re-arm an effect.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onLoggedRef = useRef(onLogged);
  onLoggedRef.current = onLogged;
  const onFlashRef = useRef(onFlash);
  onFlashRef.current = onFlash;

  const close = useCallback(() => {
    setOpen(false);
    setView('main');
    setPicked('');
  }, []);

  const openAt = useCallback((next: View) => {
    if (fixed) setAnchor(anchorBelow(rootRef.current, MENU_WIDTH, 0));
    setView(next);
    setPicked('');
    setOpen(true);
  }, [fixed]);

  // A different lead in the same control (the bar serves the next person): start clean. The
  // confirmation line is kept: in the bar it is the only sign the last attempt landed.
  useEffect(() => {
    close();
  }, [contactId, close]);

  // Tell the host when the menu closes, however it closed.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (wasOpenRef.current && !open) onCloseRef.current?.();
    wasOpenRef.current = open;
  }, [open]);

  // The sheet's Enter on the Attempts (or Callback) cell: each new signal opens the menu once.
  const lastSignalRef = useRef(openSignal);
  useEffect(() => {
    if (openSignal === undefined || openSignal === lastSignalRef.current) return;
    lastSignalRef.current = openSignal;
    if (disabled || busyRef.current || (book && triesOff)) {
      onCloseRef.current?.();
      return;
    }
    openAt(book ? 'call_back' : 'main');
  }, [openSignal, disabled, book, triesOff, openAt]);

  // Once the fixed menu has a height, keep it inside the viewport (flip it above the control).
  useLayoutEffect(() => {
    if (!open || !fixed) return;
    const menu = menuRef.current;
    const next = anchorBelow(rootRef.current, menu?.offsetWidth || MENU_WIDTH, menu?.offsetHeight || 0);
    if (!next) return;
    setAnchor((prev) => (prev && prev.top === next.top && prev.left === next.left ? prev : next));
  }, [open, fixed, view]);

  // Outside click closes (mousedown, so a click that lands on another control still does its
  // own thing). A fixed menu also closes on a scroll or a resize: it would be left floating.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', onDown);
    if (fixed) {
      window.addEventListener('scroll', close, true);
      window.addEventListener('resize', close);
    }
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, fixed, close]);

  // Land keyboard focus in the menu when it opens or changes step.
  useEffect(() => {
    if (!open) return;
    const first = menuRef.current?.querySelector<HTMLElement>('[data-autofocus], [role="menuitem"]:not([disabled])');
    first?.focus();
  }, [open, view, anchor]);

  useEffect(() => {
    if (!flash) return undefined;
    const clear = () => setFlash(null);
    const t = window.setTimeout(clear, flash.tone === 'error' ? FLASH_MS + 4_000 : FLASH_MS);
    if (flash.at) {
      window.addEventListener('scroll', clear, true);
      window.addEventListener('resize', clear);
    }
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('scroll', clear, true);
      window.removeEventListener('resize', clear);
    };
  }, [flash]);

  const show = useCallback((tone: 'ok' | 'error', text: string) => {
    const host = onFlashRef.current;
    if (host) {
      host(tone, text);
      return;
    }
    setFlash({ tone, text, at: fixed ? anchorBelow(rootRef.current, FLASH_WIDTH, FLASH_HEIGHT) : null });
  }, [fixed]);

  /**
   * Write the attempt; on success, tell the host. A call back can be refused twice, in either
   * order: outside calling hours (422, answered by "Book it anyway" -> book_anyway) and an overlap
   * on the recruiter's calendar (409 overbooked, answered by "Override and book" -> overbook_ok).
   * Each is asked once, so at most three sends.
   */
  const log = useCallback(async (outcome: RecruitAttemptOutcome, extra: Partial<RecruitAttemptRequest> = {}) => {
    if (busyRef.current || disabled) return;
    if (triesOff && TRY_OUTCOMES.has(outcome)) return;
    busyRef.current = true;
    setBusy(outcome);
    setFlash(null);
    const release = () => {
      busyRef.current = false;
      setBusy(null);
    };
    let body: RecruitAttemptRequest = { ...extra, outcome };
    let res: RecruitAttemptResponse | null = null;
    for (let round = 0; round < 3 && !res; round += 1) {
      try {
        res = await logRecruitAttempt(studyId, contactId, body);
      } catch (e: unknown) {
        const status = (e as { response?: { status?: number } } | null)?.response?.status;
        const code = recruitErrorCode(e);
        // The booking path answers an outside-hours time with a 422 and the hours in words; it
        // is only booked after an explicit "anyway" (the OutcomeControl rule).
        const outsideHours = outcome === 'call_back' && status === 422
          && (code === 'outside_hours' || !code) && !body.book_anyway;
        if (outsideHours) {
          release();
          const ok = await confirm({
            title: 'That time is outside calling hours',
            message: `${recruitErrorMessage(e, 'That time is outside calling hours.')} Only book it if they asked for that time.`,
            confirmLabel: 'Book it anyway',
          });
          if (!ok || busyRef.current) return;
          busyRef.current = true;
          setBusy(outcome);
          body = { ...body, book_anyway: true };
        } else if (outcome === 'call_back' && !body.overbook_ok && overbookConflictsOf(e)) {
          // The recruiter already has something at that time: say what, and book on top of it
          // only after an explicit override (no note needed; the owner's rule).
          const conflicts = overbookConflictsOf(e) ?? [];
          release();
          const ok = await confirm({
            title: 'That time is already booked',
            message: (
              <div className="space-y-1.5">
                <p>{conflictHeadline(null, !body.callback?.assignee_user_id)}</p>
                {conflicts.length > 0 && (
                  <ul className="list-disc space-y-0.5 pl-5">
                    {conflicts.map((c, i) => <li key={i}>{conflictLine(c)}</li>)}
                  </ul>
                )}
                <p>Override to book it anyway.</p>
              </div>
            ),
            confirmLabel: 'Override and book',
          });
          if (!ok || busyRef.current) return;
          busyRef.current = true;
          setBusy(outcome);
          body = { ...body, overbook_ok: true };
        } else {
          // The menu closes on a failure too: the line that says so only shows under a closed menu.
          close();
          show('error', recruitErrorMessage(e, "That wasn't saved. Please try again."));
          release();
          return;
        }
      }
    }
    if (!res) {
      release();
      return;
    }
    close();
    show('ok', attemptConfirmation(outcome, res, { maxAttempts, slotStart: body.callback?.slot_start ?? null }));
    notifyStatsChanged(); // the sidebar badges and Today's counts moved
    try {
      await onLoggedRef.current?.(outcome, res, body);
    } catch (err: unknown) {
      console.error('[recruit] after-attempt step failed:', (err as Error | null)?.message);
    } finally {
      release();
    }
  }, [disabled, triesOff, studyId, contactId, confirm, close, show, maxAttempts]);

  /** A menu row was chosen: log it, or open the step that asks for the missing fact. */
  const choose = useCallback(async (outcome: RecruitAttemptOutcome) => {
    if (outcome === 'call_back' || outcome === 'try_later') {
      setView(outcome);
      setPicked('');
      return;
    }
    if (outcome === 'asked_to_stop') {
      close();
      const ok = await confirm({
        title: 'They asked us not to contact them?',
        message: 'They will be opted out of texts and marked "Do not contact". Their status on this study does not change: set it yourself if they are done.',
        confirmLabel: 'Yes, they asked us to stop',
        danger: true,
      });
      if (ok) await log('asked_to_stop');
      return;
    }
    await log(outcome);
  }, [close, confirm, log]);

  // Keyboard: Escape backs out one step (then closes); 1 to 7 pick a row on the main step;
  // arrows move between rows. Handled here and stopped, so the sheet's own keys stay out of it.
  const onMenuKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (view !== 'main' && !book) setView('main');
      else close();
      return;
    }
    const target = e.target as HTMLElement;
    const typing = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
    if (view === 'main' && !typing && /^[1-9]$/.test(e.key)) {
      const row = ATTEMPT_MENU[Number(e.key) - 1];
      if (row && !(triesOff && TRY_OUTCOMES.has(row.value))) {
        e.preventDefault();
        e.stopPropagation();
        void choose(row.value);
      }
      return;
    }
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !typing) {
      const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])') ?? []);
      if (!items.length) return;
      e.preventDefault();
      e.stopPropagation();
      const at = items.indexOf(document.activeElement as HTMLElement);
      const next = e.key === 'ArrowDown' ? (at + 1) % items.length : (at - 1 + items.length) % items.length;
      items[next]?.focus();
    }
  };

  // Focus leaving the control for somewhere else (Tab back to the sheet) closes the menu.
  const onRootBlur = (e: ReactFocusEvent<HTMLDivElement>) => {
    const next = e.relatedTarget as Node | null;
    if (open && next && !e.currentTarget.contains(next)) close();
  };

  const pickProblem = view === 'call_back' || view === 'try_later'
    ? (picked ? pickedTimeProblem(picked, view) : null)
    : null;

  const logPicked = (iso: string) => {
    if (view === 'call_back') void log('call_back', { callback: { slot_start: iso } });
    else if (view === 'try_later') void log('try_later', { retry_at: iso });
  };

  const backRow = (title: string) => (
    <div className="flex items-center gap-1.5 px-1 pb-1">
      <button
        type="button"
        onClick={() => (book ? close() : setView('main'))}
        aria-label={book ? 'Close' : 'Back to all outcomes'}
        className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200 sm:h-7 sm:w-7"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      </button>
      <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">{title}</span>
    </div>
  );

  const timeStep = (kind: 'call_back' | 'try_later') => {
    const picks = kind === 'call_back' ? callbackPicks() : retryPicks();
    return (
      <>
        {backRow(kind === 'call_back' ? 'Call them back at…' : 'Try them again…')}
        <p className="px-2.5 pb-1 text-[11px] leading-snug text-gray-500 dark:text-gray-400">
          {kind === 'call_back'
            ? 'Books a real call: it shows on Today, the calendar and the reminders.'
            : 'Moves them to the bottom until then. A text from them brings them back sooner.'}
        </p>
        {picks.map((p) => (
          <button
            key={p.label}
            type="button"
            role="menuitem"
            onClick={() => logPicked(p.iso)}
            disabled={!!busy}
            className={menuItemCls}
          >
            {kind === 'call_back'
              ? <CalendarClock className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
              : <Clock className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />}
            {p.label}
            <span className="ml-auto flex-shrink-0 text-[11px] font-normal text-gray-400 dark:text-gray-500">{whenLabel(p.iso)}</span>
          </button>
        ))}
        <div className="mt-1 border-t border-gray-100 px-2.5 pb-1 pt-2 dark:border-gray-700">
          <label htmlFor={`${menuId}-when`} className="block text-[11px] font-medium text-gray-500 dark:text-gray-400">
            Pick a day and time
          </label>
          <input
            id={`${menuId}-when`}
            type="datetime-local"
            value={picked}
            min={toLocalInput(new Date())}
            onChange={(e) => setPicked(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && picked && !pickedTimeProblem(picked, kind)) {
                e.preventDefault();
                e.stopPropagation();
                logPicked(new Date(picked).toISOString());
              }
            }}
            className={`mt-1 ${fieldCls}`}
          />
          {pickProblem && <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">{pickProblem}</p>}
          <button
            type="button"
            onClick={() => logPicked(new Date(picked).toISOString())}
            disabled={!picked || !!pickProblem || !!busy}
            className="mt-1.5 inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
          >
            {busy === kind ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            {kind === 'call_back' ? 'Book the call' : 'Try them then'}
          </button>
        </div>
      </>
    );
  };

  const itemRow = (m: (typeof ATTEMPT_MENU)[number]) => {
    const n = ATTEMPT_MENU.indexOf(m) + 1;
    const off = triesOff && TRY_OUTCOMES.has(m.value);
    return (
      <button
        key={m.value}
        type="button"
        role="menuitem"
        onClick={() => { void choose(m.value); }}
        disabled={!!busy || off}
        title={off ? triesDisabledReason || undefined : undefined}
        className={`${menuItemCls} ${m.value === 'asked_to_stop' ? 'text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20' : ''}`}
      >
        {busy === m.value
          ? <Loader2 className="h-3.5 w-3.5 flex-shrink-0 animate-spin text-gray-400" aria-hidden="true" />
          : <span className="w-3.5 flex-shrink-0 text-center text-[10px] font-semibold tabular-nums text-gray-400 dark:text-gray-500" aria-hidden="true">{n}</span>}
        {m.label}
      </button>
    );
  };

  const tries = ATTEMPT_MENU.filter((m) => TRY_OUTCOMES.has(m.value));
  const stops = ATTEMPT_MENU.filter((m) => !TRY_OUTCOMES.has(m.value));
  const size = SIZE[surface];
  // The bar surface: under the control from md up, under the whole (translated) bar below md.
  const edge = fixed ? '' : 'absolute right-0 top-full mt-1.5 max-md:fixed max-md:inset-x-0 max-md:ml-auto';
  const menuStyle: CSSProperties | undefined = fixed && anchor
    ? { position: 'fixed', top: anchor.top, left: anchor.left }
    : undefined;
  const flashStyle: CSSProperties | undefined = flash?.at
    ? { position: 'fixed', top: flash.at.top, left: flash.at.left }
    : undefined;
  const who = name ? ` with ${name}` : '';

  return (
    <div ref={rootRef} className="relative inline-flex flex-shrink-0" onBlur={onRootBlur}>
      {book ? (
        <button
          type="button"
          onClick={() => (open ? close() : openAt('call_back'))}
          disabled={disabled || !!busy || triesOff}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          title={triesDisabledReason || `Book a call back${who}`}
          className={BOOK_CLS}
        >
          {busy
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            : <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />}
          Book
        </button>
      ) : (
        <div className="inline-flex" role="group" aria-label={`Log what happened${who}`}>
          <button
            type="button"
            onClick={() => { void log('no_answer'); }}
            disabled={disabled || !!busy || triesOff}
            title={triesDisabledReason || `Log "${ATTEMPT_LABELS.no_answer}"${who}`}
            className={`inline-flex items-center justify-center gap-1 font-medium transition-colors duration-150 focus:z-10 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 ${size.main}`}
          >
            {busy === 'no_answer'
              ? <Loader2 className="h-3.5 w-3.5 flex-shrink-0 animate-spin" aria-hidden="true" />
              : <PhoneOff className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />}
            <span className="truncate">{ATTEMPT_LABELS.no_answer}</span>
          </button>
          <button
            type="button"
            onClick={() => (open ? close() : openAt('main'))}
            disabled={disabled || !!busy}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={open ? menuId : undefined}
            title="Log a different outcome"
            className={`inline-flex items-center gap-1 font-medium transition-colors duration-150 focus:z-10 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 ${size.more}`}
          >
            <span className="sr-only">Log outcome</span>
            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      )}

      {open && (!fixed || anchor) && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={`Log what happened${who}`}
          onKeyDown={onMenuKey}
          style={menuStyle}
          className={`${edge} w-72 max-w-[calc(100vw-1.5rem)] ${Z.overlay} rounded-xl border border-gray-200 bg-white p-1.5 text-left font-inter shadow-lg dark:border-gray-700 dark:bg-gray-800`}
        >
          {view === 'main' && (
            <>
              {triesOff && (
                <p className="px-2.5 pb-1 pt-1 text-[11px] leading-snug text-gray-500 dark:text-gray-400">{triesDisabledReason}</p>
              )}
              <div className={groupLabelCls}>Log a try</div>
              {tries.map(itemRow)}
              <div className="mx-2.5 my-1 border-t border-gray-100 dark:border-gray-700" />
              <div className={groupLabelCls}>Stop contacting</div>
              {stops.map(itemRow)}
            </>
          )}
          {view === 'call_back' && timeStep('call_back')}
          {view === 'try_later' && timeStep('try_later')}
        </div>
      )}

      {flash && !open && (
        <div
          role={flash.tone === 'error' ? 'alert' : 'status'}
          style={flashStyle}
          className={`${flash.at ? '' : 'absolute right-0 top-full mt-2'} w-max max-w-[18rem] ${Z.overlay} flex items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-left text-xs font-normal leading-snug shadow-md ${
            flash.tone === 'error'
              ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300'
              : 'border-gray-200 bg-white text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200'
          }`}
        >
          {flash.tone === 'ok' && <Check className="mt-px h-3.5 w-3.5 flex-shrink-0 text-green-600 dark:text-green-500" aria-hidden="true" />}
          <span>{flash.text}</span>
        </div>
      )}
    </div>
  );
}

export default AttemptMenu;
